import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';

import { apiClient } from '@/config/api';
import { colors, font, space, radius } from '@/theme/chalkCourt';

/**
 * Web (PWA) call-recording player — the `.web.tsx` override of AudioPlayer.tsx.
 *
 * The recordings S3 bucket sends no CORS headers, so a direct cross-origin
 * `<audio src=s3url>` won't inline-play in the browser. Instead we fetch the
 * recording as an authenticated blob through our own backend stream endpoint
 * (`/call-performance/calls/{uuid}/recording-stream`, JWT via the axios
 * interceptor), turn it into a same-origin `blob:` URL, and play it inline with
 * native `<audio controls>` (seekable, no CORS). Native uses AudioPlayer.tsx.
 */
const AudioPlayer: React.FC<{ callUuid: string }> = ({ callUuid }) => {
  const [loading, setLoading] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Revoke the blob URL when it changes or on unmount (avoid leaks).
  useEffect(() => {
    return () => {
      if (objectUrl) {
        (globalThis as any).URL?.revokeObjectURL?.(objectUrl);
      }
    };
  }, [objectUrl]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient
        .getInstance()
        .get(`/call-performance/calls/${encodeURIComponent(callUuid)}/recording-stream`, {
          responseType: 'blob',
        });
      const url = (globalThis as any).URL.createObjectURL(res.data);
      setObjectUrl(url);
    } catch (e: any) {
      setError(e?.response?.status === 404 ? 'Recording not available.' : 'Could not load the recording.');
    } finally {
      setLoading(false);
    }
  };

  if (objectUrl) {
    return React.createElement('audio', {
      controls: true,
      autoPlay: true,
      src: objectUrl,
      style: { width: '100%', display: 'block' },
    });
  }

  return (
    <View>
      <TouchableOpacity style={styles.btn} disabled={loading} onPress={load}>
        {loading ? (
          <ActivityIndicator color={colors.onAccent} />
        ) : (
          <Text style={styles.btnText}>▶  Load Recording</Text>
        )}
      </TouchableOpacity>
      {!!error && <Text style={styles.error}>{error}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  btn: {
    backgroundColor: colors.accent,
    borderRadius: radius.pill,
    paddingVertical: space.md,
    paddingHorizontal: space.lg,
    alignItems: 'center',
  },
  btnText: {
    fontFamily: font.bodyBold,
    color: colors.onAccent,
    fontSize: 15,
  },
  error: {
    fontFamily: font.body,
    color: colors.inkSoft,
    fontSize: 14,
    marginTop: space.sm,
  },
});

export default AudioPlayer;
